import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { AlertCircle, CheckCircle, Mail, Send, Settings, Users, BarChart3, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = window.location.origin;

const GestionNotificaciones = () => {
  const [emailConfig, setEmailConfig] = useState({
    configurado: false,
    config: null
  });
  const [configForm, setConfigForm] = useState({
    email_institucional: '',
    password_email: '',
    nombre_institucion: 'Instituto Bíblico Apostólico del Uruguay'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    asunto: '',
    mensaje: '',
    tipo_destinatario: 'todos',
    roles: [],
    usuarios_ids: []
  });
  const [preferences, setPreferences] = useState({});
  const [stats, setStats] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    if (!initialLoadDone) {
      loadEmailConfig();
      loadNotificationPreferences();
      loadStats();
      loadLogs();
      loadUsuarios();
      setInitialLoadDone(true);
    }
  }, [initialLoadDone]);

  const loadEmailConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/email-config`);
      setEmailConfig(response.data);
      // Only update form if fields are currently empty (preserve user input)
      setConfigForm(prev => ({
        email_institucional: prev.email_institucional || (response.data.config?.email_institucional || ''),
        password_email: prev.password_email || '',
        nombre_institucion: prev.nombre_institucion || (response.data.config?.nombre_institucion || 'Instituto Bíblico Apostólico del Uruguay')
      }));
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const loadEmailConfigSilent = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/email-config`);
      setEmailConfig(response.data);
      // Only update non-password fields
      if (response.data.config) {
        setConfigForm(prev => ({
          ...prev,
          email_institucional: response.data.config.email_institucional || prev.email_institucional,
          nombre_institucion: response.data.config.nombre_institucion || prev.nombre_institucion
        }));
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/notifications/preferences`);
      console.log('🔍 PREFERENCES FROM API:', response.data);
      console.log('email_enabled:', response.data.email_enabled);
      console.log('noticias_inmediato:', response.data.noticias_inmediato);
      console.log('becas_aprobacion_enabled:', response.data.becas_aprobacion_enabled);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/notifications/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/notifications/logs?limit=20`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (emailConfig.configurado) {
        await axios.put(`${API_BASE_URL}/api/admin/email-config`, configForm);
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/email-config`, configForm);
      }
      alert('Configuración guardada correctamente');
      
      // Reload config but preserve password in form
      const currentPassword = configForm.password_email;
      await loadEmailConfigSilent();
      setConfigForm(prev => ({
        ...prev,
        password_email: currentPassword
      }));
      
    } catch (error) {
      alert('Error al guardar configuración');
      console.error(error);
    }
    setLoading(false);
  };

  const testEmailConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/email-config/test`);
      if (response.data.enviado) {
        alert('✅ Email de prueba enviado correctamente. Revisa tu bandeja de entrada.');
        // Reload logs to show the test email
        await loadLogs();
        await loadStats();
      } else {
        alert('❌ Error al enviar email de prueba: ' + (response.data.error || 'Error desconocido'));
      }
    } catch (error) {
      alert('❌ Error al probar configuración de email: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    }
    setLoading(false);
  };

  const sendBulkNotification = async (e) => {
    e.preventDefault();
    if (!notificationForm.asunto || !notificationForm.mensaje) {
      alert('Por favor completa el asunto y mensaje');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        asunto: notificationForm.asunto,
        mensaje: notificationForm.mensaje,
        tipo_destinatario: notificationForm.tipo_destinatario
      };

      if (notificationForm.tipo_destinatario === 'role') {
        payload.roles = notificationForm.roles;
      } else if (notificationForm.tipo_destinatario === 'usuarios_especificos') {
        payload.usuarios_ids = notificationForm.usuarios_ids;
      }

      const response = await axios.post(`${API_BASE_URL}/api/notifications/bulk`, payload);
      alert(`Notificación enviada: ${response.data.enviados} exitosos, ${response.data.fallidos} fallidos`);
      
      // Reset form
      setNotificationForm({
        asunto: '',
        mensaje: '',
        tipo_destinatario: 'todos',
        roles: [],
        usuarios_ids: []
      });
      
      loadStats();
      loadLogs();
    } catch (error) {
      alert('Error al enviar notificación');
      console.error(error);
    }
    setLoading(false);
  };

  const updatePreferences = async (field, value) => {
    try {
      const payload = { [field]: value };
      await axios.put(`${API_BASE_URL}/api/notifications/preferences`, payload);
      setPreferences(prev => ({ ...prev, [field]: value }));
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-UY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (estado) => {
    const variants = {
      'enviado': { variant: 'default', color: 'bg-green-500', text: 'Enviado' },
      'fallido': { variant: 'destructive', color: 'bg-red-500', text: 'Fallido' },
      'pendiente': { variant: 'secondary', color: 'bg-yellow-500', text: 'Pendiente' }
    };
    
    const config = variants[estado] || variants['pendiente'];
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.text}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Gestión de Notificaciones</h1>
        <Badge variant="outline" className="text-sm">
          <Mail className="w-4 h-4 mr-2" />
          Sistema de Email
        </Badge>
      </div>

      <Tabs defaultValue="configuracion" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="configuracion">Configuración</TabsTrigger>
          <TabsTrigger value="enviar">Enviar Notificación</TabsTrigger>
          <TabsTrigger value="preferencias">Preferencias</TabsTrigger>
          <TabsTrigger value="estadisticas">Estadísticas</TabsTrigger>
        </TabsList>

        <TabsContent value="configuracion" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Email Institucional
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConfigSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email_institucional">Email Institucional</Label>
                    <Input
                      id="email_institucional"
                      type="email"
                      value={configForm.email_institucional}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, email_institucional: e.target.value }))}
                      placeholder="instituto@gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nombre_institucion">Nombre de la Institución</Label>
                    <Input
                      id="nombre_institucion"
                      value={configForm.nombre_institucion}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, nombre_institucion: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                
                <div className="relative">
                  <Label htmlFor="password_email">Contraseña de Aplicación (Gmail)</Label>
                  <div className="relative">
                    <Input
                      id="password_email"
                      type={showPassword ? "text" : "password"}
                      value={configForm.password_email}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, password_email: e.target.value }))}
                      placeholder={emailConfig.configurado ? "Contraseña configurada ✓ (Ingresa nueva para cambiar)" : "Contraseña de aplicación de Gmail"}
                      required={!emailConfig.configurado}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Necesitas generar una "Contraseña de aplicación" en tu cuenta de Gmail para usar SMTP
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Guardando...' : emailConfig.configurado ? 'Actualizar Configuración' : 'Guardar Configuración'}
                  </Button>
                  {emailConfig.configurado && (
                    <Button type="button" variant="outline" onClick={testEmailConfig} disabled={loading}>
                      Probar Email
                    </Button>
                  )}
                </div>
              </form>

              {emailConfig.configurado && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Email configurado correctamente</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Último envío: {emailConfig.config?.ultimo_envio ? formatDate(emailConfig.config.ultimo_envio) : 'Nunca'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enviar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Enviar Notificación Masiva
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!emailConfig.configurado ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Configuración requerida</span>
                  </div>
                  <p className="text-sm text-yellow-600 mt-1">
                    Debes configurar el email institucional antes de enviar notificaciones.
                  </p>
                </div>
              ) : (
                <form onSubmit={sendBulkNotification} className="space-y-4">
                  <div>
                    <Label htmlFor="asunto">Asunto del Email</Label>
                    <Input
                      id="asunto"
                      value={notificationForm.asunto}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, asunto: e.target.value }))}
                      placeholder="Ej: Recordatorio: Tarea pendiente"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mensaje">Mensaje</Label>
                    <Textarea
                      id="mensaje"
                      value={notificationForm.mensaje}
                      onChange={(e) => setNotificationForm(prev => ({ ...prev, mensaje: e.target.value }))}
                      placeholder="Escribe el contenido del mensaje..."
                      rows={5}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="tipo_destinatario">Destinatarios</Label>
                    <Select
                      value={notificationForm.tipo_destinatario}
                      onValueChange={(value) => setNotificationForm(prev => ({ ...prev, tipo_destinatario: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos los usuarios</SelectItem>
                        <SelectItem value="role">Por rol</SelectItem>
                        <SelectItem value="usuarios_especificos">Usuarios específicos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {notificationForm.tipo_destinatario === 'role' && (
                    <div>
                      <Label>Seleccionar Roles</Label>
                      <div className="flex gap-2 mt-2">
                        {['admin', 'profesor', 'estudiante'].map(role => (
                          <label key={role} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={notificationForm.roles.includes(role)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNotificationForm(prev => ({
                                    ...prev,
                                    roles: [...prev.roles, role]
                                  }));
                                } else {
                                  setNotificationForm(prev => ({
                                    ...prev,
                                    roles: prev.roles.filter(r => r !== role)
                                  }));
                                }
                              }}
                            />
                            <span className="capitalize">{role}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {notificationForm.tipo_destinatario === 'usuarios_especificos' && (
                    <div>
                      <Label>Seleccionar Usuarios</Label>
                      <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-3 space-y-2">
                        {usuarios.map(user => (
                          <label key={user.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notificationForm.usuarios_ids.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNotificationForm(prev => ({
                                    ...prev,
                                    usuarios_ids: [...prev.usuarios_ids, user.id]
                                  }));
                                } else {
                                  setNotificationForm(prev => ({
                                    ...prev,
                                    usuarios_ids: prev.usuarios_ids.filter(id => id !== user.id)
                                  }));
                                }
                              }}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{user.nombre}</div>
                              <div className="text-xs text-gray-500">{user.email}</div>
                            </div>
                            <Badge variant="outline" className="text-xs capitalize">
                              {user.rol}
                            </Badge>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {notificationForm.usuarios_ids.length} usuario(s) seleccionado(s)
                      </p>
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? 'Enviando...' : 'Enviar Notificación'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferencias" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Preferencias de Notificación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* DEBUG INFO - VALORES RAW */}
              <div className="bg-yellow-100 border border-yellow-400 p-4 rounded mb-4">
                <h4 className="font-bold text-sm mb-2">🔍 DEBUG - Valores desde API:</h4>
                <pre className="text-xs">
                  email_enabled: {String(preferences.email_enabled)}{'\n'}
                  noticias_inmediato: {String(preferences.noticias_inmediato)}{'\n'}
                  becas_aprobacion_enabled: {String(preferences.becas_aprobacion_enabled)}{'\n'}
                  becas_rechazo_enabled: {String(preferences.becas_rechazo_enabled)}{'\n'}
                  becas_vencimiento_enabled: {String(preferences.becas_vencimiento_enabled)}
                </pre>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Recibir notificaciones por email</Label>
                  <p className="text-sm text-gray-500">Activar/desactivar todas las notificaciones por email</p>
                </div>
                <Switch
                  checked={preferences.email_enabled === true}
                  onCheckedChange={(checked) => updatePreferences('email_enabled', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Anuncios inmediatos</Label>
                  <p className="text-sm text-gray-500">Recibir nuevos anuncios inmediatamente</p>
                </div>
                <Switch
                  checked={preferences.noticias_inmediato === true}
                  onCheckedChange={(checked) => updatePreferences('noticias_inmediato', checked)}
                />
              </div>

              {/* Notificaciones de Becas */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3 text-purple-700">🎓 Notificaciones de Becas</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Beca Aprobada</Label>
                      <p className="text-sm text-gray-500">Enviar email al estudiante cuando se aprueba una beca</p>
                    </div>
                    <Switch
                      checked={preferences.becas_aprobacion_enabled === true}
                      onCheckedChange={(checked) => updatePreferences('becas_aprobacion_enabled', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Beca Rechazada</Label>
                      <p className="text-sm text-gray-500">Enviar email al estudiante cuando se rechaza una beca</p>
                    </div>
                    <Switch
                      checked={preferences.becas_rechazo_enabled === true}
                      onCheckedChange={(checked) => updatePreferences('becas_rechazo_enabled', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Recordatorio de Vencimiento</Label>
                      <p className="text-sm text-gray-500">Recordar al estudiante 10 días antes de que venza su beca</p>
                    </div>
                    <Switch
                      checked={preferences.becas_vencimiento_enabled === true}
                      onCheckedChange={(checked) => updatePreferences('becas_vencimiento_enabled', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Clases del Día</Label>
                      <p className="text-sm text-gray-500">Enviar recordatorio diario con las clases del día</p>
                    </div>
                    <Switch
                      checked={preferences.recordatorio_clases_dia === true}
                      onCheckedChange={(checked) => updatePreferences('recordatorio_clases_dia', checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Días de anticipación para tareas</Label>
                  <Select
                    value={preferences.tareas_vencer_dias?.toString() || '3'}
                    onValueChange={(value) => updatePreferences('tareas_vencer_dias', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 día</SelectItem>
                      <SelectItem value="2">2 días</SelectItem>
                      <SelectItem value="3">3 días</SelectItem>
                      <SelectItem value="5">5 días</SelectItem>
                      <SelectItem value="7">7 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Días de anticipación para formularios</Label>
                  <Select
                    value={preferences.formularios_vencer_dias?.toString() || '2'}
                    onValueChange={(value) => updatePreferences('formularios_vencer_dias', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 día</SelectItem>
                      <SelectItem value="2">2 días</SelectItem>
                      <SelectItem value="3">3 días</SelectItem>
                      <SelectItem value="5">5 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="estadisticas" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-600">Enviados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-700">{stats.total_enviados || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-600">Fallidos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-700">{stats.total_fallidos || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-yellow-600">Pendientes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-700">{stats.total_pendientes || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-600">Últimos 7 días</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-700">{stats.actividad_reciente_7_dias || 0}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Registro de Notificaciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Tipo</th>
                      <th className="text-left p-2">Asunto</th>
                      <th className="text-left p-2">Destinatario</th>
                      <th className="text-left p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">{formatDate(log.fecha_creacion)}</td>
                        <td className="p-2">
                          <Badge variant="outline" className="text-xs">
                            {log.tipo_notificacion}
                          </Badge>
                        </td>
                        <td className="p-2 max-w-xs truncate">{log.asunto}</td>
                        <td className="p-2">{log.email_destinatario}</td>
                        <td className="p-2">{getStatusBadge(log.estado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {logs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No hay registros de notificaciones
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GestionNotificaciones;